import { MedicationFormData, StepValidationFn } from '@/components/medication/types/medication';

/**
 * Validates step 1 of the medication form (basic details)
 * 
 * @param formData Current medication form data
 * @returns boolean indicating if step 1 is valid
 */
const validateStep1: StepValidationFn = (formData: MedicationFormData): boolean => {
  return !!(
    formData.name && 
    formData.name.trim() !== '' && 
    formData.dosage && 
    formData.dosage.trim() !== '' &&
    formData.form &&
    formData.form.trim() !== ''
  );
};

/**
 * Validates step 2 of the medication form (schedule)
 * 
 * @param formData Current medication form data
 * @returns boolean indicating if step 2 is valid
 */
const validateStep2: StepValidationFn = (formData: MedicationFormData): boolean => {
  return !!(
    formData.frequency && 
    formData.frequency.trim() !== '' &&
    formData.timeOfDay && 
    Array.isArray(formData.timeOfDay) && 
    formData.timeOfDay.length > 0
  );
};

/**
 * Validates step 3 of the medication form (cycle)
 * 
 * @param formData Current medication form data
 * @returns boolean indicating if step 3 is valid
 */
const validateStep3: StepValidationFn = (formData: MedicationFormData): boolean => {
  // If cycle is not enabled, the step is automatically valid
  if (!formData.hasCycle) {
    return true;
  }

  // If cycle is enabled, weeks on and weeks off must be valid numbers greater than 0
  return !!(
    typeof formData.cycleWeeksOn === 'number' && 
    formData.cycleWeeksOn > 0 &&
    typeof formData.cycleWeeksOff === 'number' && 
    formData.cycleWeeksOff > 0
  );
};

/**
 * Validates step 4 of the medication form (additional options)
 * This step is always valid as all fields are optional
 * 
 * @param formData Current medication form data
 * @returns boolean indicating if step 4 is valid (always true)
 */
const validateStep4: StepValidationFn = (_: MedicationFormData): boolean => {
  // All fields in step 4 are optional, so this is always valid
  return true;
};

/**
 * Gets the appropriate validation function for the current step
 * 
 * @param step Current step number
 * @param formData Current medication form data
 * @returns boolean indicating if the step is valid
 */
export const getStepValidation = (step: number, formData: MedicationFormData): boolean => {
  switch (step) {
    case 1:
      return validateStep1(formData);
    case 2:
      return validateStep2(formData);
    case 3:
      return validateStep3(formData);
    case 4:
      return validateStep4(formData);
    default:
      return false;
  }
};

/**
 * Validates the entire form
 * 
 * @param formData Current medication form data
 * @returns boolean indicating if all steps are valid
 */
export const validateForm = (formData: MedicationFormData): boolean => {
  return (
    validateStep1(formData) &&
    validateStep2(formData) &&
    validateStep3(formData) &&
    validateStep4(formData)
  );
};
